'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { IPlugin } from '@/types';
import { TrustedBy } from '@/components/trustedBy/trustedBy.comp';
import { HiOutlineCurrencyDollar } from '@react-icons/all-files/hi/HiOutlineCurrencyDollar';
import { HiOutlineStar } from '@react-icons/all-files/hi/HiOutlineStar';
import { HiOutlineChartBar } from '@react-icons/all-files/hi/HiOutlineChartBar';
import { HiOutlinePhone } from '@react-icons/all-files/hi/HiOutlinePhone';
import { HiOutlineClock } from '@react-icons/all-files/hi/HiOutlineClock';
import { HiOutlineSearch } from '@react-icons/all-files/hi/HiOutlineSearch';
import {
  IVerticalHeroConfig,
  IIndustryStat,
  IPainPointRow,
  IWidgetShowcaseItem,
  IIndustryInsight,
  IIndustryNarrative,
  IRoiExample,
  IBrowserMockupConfig,
} from './verticalData';

const INSIGHT_ICONS: Record<string, React.ReactNode> = {
  '💰': <HiOutlineCurrencyDollar />,
  '⭐': <HiOutlineStar />,
  '📊': <HiOutlineChartBar />,
  '📱': <HiOutlinePhone />,
  '⏱️': <HiOutlineClock />,
  '🔍': <HiOutlineSearch />,
};
import { WIDGET_PREVIEW_MAP } from './WidgetPreviews.comp';
import {
  StyledSplitHero,
  StyledHeroInner,
  StyledHeroLeft,
  StyledHeroCtas,
  StyledTrustBadges,
  StyledHeroRight,
  StyledBrowserMockup,
  StyledHeroFloat,
  StyledSection,
  StyledInner,
  StyledEyebrow,
  StyledSectionTitle,
  StyledSectionSub,
  StyledStatsGrid,
  StyledStatCell,
  StyledSourcesNote,
  StyledNarrative,
  StyledInsightsGrid,
  StyledInsightCard,
  StyledComparisonTable,
  StyledComparisonCol,
  StyledComparisonRow,
  StyledShowcaseGrid,
  StyledShowcaseCard,
  StyledUseCaseBadge,
  StyledShowcaseFooter,
  StyledRoiTable,
  StyledRoiNarrative,
} from './VerticalHub.styles';

interface IProps {
  hero: IVerticalHeroConfig;
  industryStats: IIndustryStat[];
  industryNarrative?: IIndustryNarrative;
  industryInsights?: IIndustryInsight[];
  painPointComparison: IPainPointRow[];
  widgetShowcase: IWidgetShowcaseItem[];
  featuredWidgets?: IPlugin[];
  roiExamples?: IRoiExample[];
  roiNarrative?: string[];
  statsSources?: string;
  mockupConfig?: IBrowserMockupConfig;
}

export const VerticalHub = ({
  hero,
  industryStats,
  industryNarrative,
  industryInsights,
  painPointComparison,
  widgetShowcase,
  featuredWidgets,
  roiExamples,
  roiNarrative,
  statsSources,
  mockupConfig,
}: IProps) => {
  const widgetLogoMap = new Map(
    (featuredWidgets || []).map((w) => [w.slug, w.logo])
  );

  return (
    <>
      {/* ─── SPLIT HERO ─── */}
      <StyledSplitHero>
        <StyledHeroInner>
          <StyledHeroLeft>
            <h1>
              {hero.headline.split(hero.highlightWord).map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span className="highlight">{hero.highlightWord}</span>
                  )}
                </React.Fragment>
              ))}
            </h1>
            <p className="hero-sub">{hero.subtitle}</p>
            <StyledHeroCtas>
              <Link href={hero.primaryCta.href} className="cta-primary">
                {hero.primaryCta.text}
              </Link>
              {hero.secondaryCta && (
                <a href={hero.secondaryCta.href} className="cta-secondary">
                  {hero.secondaryCta.text}
                </a>
              )}
            </StyledHeroCtas>
            <StyledTrustBadges>
              {hero.trustBadges.map((badge) => (
                <span key={badge}>{badge}</span>
              ))}
            </StyledTrustBadges>
          </StyledHeroLeft>

          <StyledHeroRight>
            <IndustryBrowserMockup config={mockupConfig} />
          </StyledHeroRight>
        </StyledHeroInner>
      </StyledSplitHero>

      {/* ─── TRUSTED BY ─── */}
      <TrustedBy mode="light" />

      {/* ─── INDUSTRY STATS ─── */}
      <StyledSection $bg="#f8faff">
        <StyledInner>
          <div style={{ textAlign: 'center' }}>
            <StyledEyebrow>The Opportunity</StyledEyebrow>
            <StyledSectionTitle>Your Website Is Your #1 Sales Tool</StyledSectionTitle>
            <StyledSectionSub $center>
              The market is massive — but most websites in this space are losing leads every day.
            </StyledSectionSub>
          </div>
          <StyledStatsGrid>
            {industryStats.map((s, i) => (
              <StyledStatCell key={i}>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </StyledStatCell>
            ))}
          </StyledStatsGrid>
          {statsSources && <StyledSourcesNote>{statsSources}</StyledSourcesNote>}
        </StyledInner>
      </StyledSection>

      {/* ─── INDUSTRY NARRATIVE (long-form content) ─── */}
      {industryNarrative && (
        <StyledSection>
          <StyledInner>
            <StyledEyebrow>{industryNarrative.eyebrow}</StyledEyebrow>
            <StyledSectionTitle>{industryNarrative.title}</StyledSectionTitle>
            <StyledNarrative>
              {industryNarrative.paragraphs.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </StyledNarrative>
          </StyledInner>
        </StyledSection>
      )}

      {/* ─── INDUSTRY INSIGHTS (fun-fact callouts) ─── */}
      {industryInsights && industryInsights.length > 0 && (
        <StyledSection $bg="#f8faff">
          <StyledInner>
            <div style={{ textAlign: 'center' }}>
              <StyledEyebrow>Did You Know?</StyledEyebrow>
              <StyledSectionTitle>
                Why Interactive Websites Win More Customers
              </StyledSectionTitle>
              <StyledSectionSub $center>
                The data is clear: businesses that add interactive tools to their
                websites convert significantly more visitors into paying customers.
              </StyledSectionSub>
            </div>
            <StyledInsightsGrid>
              {industryInsights.map((insight, i) => (
                <StyledInsightCard key={i}>
                  <div className="insight-icon">{INSIGHT_ICONS[insight.icon] || insight.icon}</div>
                  <div className="insight-stat">{insight.stat}</div>
                  <div className="insight-headline">{insight.headline}</div>
                  <p className="insight-body">{insight.body}</p>
                  {insight.source && (
                    <p className="insight-source">{insight.source}</p>
                  )}
                </StyledInsightCard>
              ))}
            </StyledInsightsGrid>
          </StyledInner>
        </StyledSection>
      )}

      {/* ─── PAIN POINTS COMPARISON ─── */}
      <StyledSection>
        <StyledInner>
          <StyledEyebrow>The Problem</StyledEyebrow>
          <StyledSectionTitle>
            Your Website Should Be Working as Hard as You Do
          </StyledSectionTitle>
          <StyledSectionSub>
            Most websites in this space are digital brochures. They look OK, but they
            don&apos;t convert visitors into leads. Here&apos;s what separates the
            top-performing sites from the rest.
          </StyledSectionSub>
          <StyledComparisonTable>
            <StyledComparisonCol $type="pain">
              <div className="col-header">What Most Websites Do</div>
              <div className="col-items">
                {painPointComparison.map((row, i) => (
                  <StyledComparisonRow key={i} $type="pain">
                    <span className="row-icon">&#10060;</span>
                    {row.pain}
                  </StyledComparisonRow>
                ))}
              </div>
            </StyledComparisonCol>
            <StyledComparisonCol $type="fix">
              <div className="col-header">What Actually Converts</div>
              <div className="col-items">
                {painPointComparison.map((row, i) => (
                  <StyledComparisonRow key={i} $type="fix">
                    <span className="row-icon">&#9989;</span>
                    {row.fix}
                  </StyledComparisonRow>
                ))}
              </div>
            </StyledComparisonCol>
          </StyledComparisonTable>
        </StyledInner>
      </StyledSection>

      {/* ─── WIDGET SHOWCASE ─── */}
      <StyledSection $bg="#f8faff" id="widgets">
        <StyledInner>
          <div style={{ textAlign: 'center' }}>
            <StyledEyebrow>Widget Showcase</StyledEyebrow>
            <StyledSectionTitle>
              Hand-Picked Widgets for Your Industry
            </StyledSectionTitle>
            <StyledSectionSub $center>
              Every widget is free to start, takes minutes to embed, and is chosen
              specifically for businesses like yours.
            </StyledSectionSub>
          </div>
          <StyledShowcaseGrid>
            {widgetShowcase.map((item) => {
              const PreviewComponent = WIDGET_PREVIEW_MAP[item.slug];
              const logo = widgetLogoMap.get(item.slug);
              return (
              <StyledShowcaseCard key={item.slug}>
                <div className="card-preview">
                  {PreviewComponent ? <PreviewComponent /> : (item.emoji || '🔧')}
                </div>
                <div className="card-body">
                  <div className="card-header">
                    {logo ? (
                      <Image src={logo} alt={item.displayName} width={40} height={40} className="widget-logo" />
                    ) : (
                      <div className="widget-logo-placeholder">{item.emoji || '🔧'}</div>
                    )}
                    <div className="card-name">{item.displayName}</div>
                  </div>
                  <p className="card-desc">{item.description}</p>
                  {item.subVerticalExample && (
                    <StyledUseCaseBadge>
                      <strong>{item.subVerticalExample}:</strong>{' '}
                      {item.industryUseCase}
                    </StyledUseCaseBadge>
                  )}
                  <Link href={`/widgets/${item.slug}`} className="card-link">
                    Add to your site &rarr;
                  </Link>
                </div>
              </StyledShowcaseCard>
              );
            })}
          </StyledShowcaseGrid>
          <StyledShowcaseFooter>
            <p className="footer-note">
              Showing {widgetShowcase.length} of 200+ available widgets
            </p>
            <Link href="/widgets">Browse All Widgets &rarr;</Link>
          </StyledShowcaseFooter>
        </StyledInner>
      </StyledSection>

      {/* ─── ROI DEEP-DIVE ─── */}
      {roiExamples && roiExamples.length > 0 && (
        <StyledSection>
          <StyledInner>
            <StyledEyebrow>Your ROI</StyledEyebrow>
            <StyledSectionTitle>
              The Numbers Behind Interactive Widgets
            </StyledSectionTitle>
            <StyledSectionSub>
              Here&apos;s what happens when a typical website adds interactive tools.
              Same traffic, dramatically different results.
            </StyledSectionSub>
            <StyledRoiTable>
              <table>
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Before Widgets</th>
                    <th>After Widgets</th>
                  </tr>
                </thead>
                <tbody>
                  {roiExamples.map((row, i) => (
                    <tr key={i} className={row.highlight ? 'row-highlight' : ''}>
                      <td className="col-label">{row.label}</td>
                      <td>{row.before}</td>
                      <td className="col-highlight">{row.after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StyledRoiTable>
            {roiNarrative && (
              <StyledRoiNarrative>
                {roiNarrative.map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </StyledRoiNarrative>
            )}
          </StyledInner>
        </StyledSection>
      )}
    </>
  );
};

/* ─── Configurable browser mockup per industry ─── */

const DEFAULT_MOCKUP: IBrowserMockupConfig = {
  url: 'yourbusiness.com',
  businessName: 'Your Business',
  tagline: 'Grow with interactive widgets',
  ctaText: 'Get Started',
  widgets: [
    { label: 'Reviews', rows: [{ left: '&#9733;&#9733;&#9733;&#9733;&#9733;', right: '' }] },
    { label: 'Hours', rows: [{ left: 'Mon', right: '9am-5pm' }] },
  ],
  floatStat: '+143 new leads from widgets',
};

function IndustryBrowserMockup({ config }: { config?: IBrowserMockupConfig }) {
  const c = config || DEFAULT_MOCKUP;
  return (
    <StyledBrowserMockup>
      <div className="browser-bar">
        <span className="b-dot" style={{ background: '#ff5f57' }} />
        <span className="b-dot" style={{ background: '#ffbd2e' }} />
        <span className="b-dot" style={{ background: '#28c840' }} />
        <div className="browser-url">{c.url}</div>
      </div>
      <div className="browser-content">
        <div className="mock-banner">
          <h3>{c.businessName}</h3>
          <p>{c.tagline}</p>
          <span className="mock-cta">{c.ctaText} &rarr;</span>
        </div>
        {c.notificationText && (
          <div className="mock-notif">
            {c.notificationText}
            <span className="notif-cta">Claim</span>
          </div>
        )}
        <div className="mock-widgets">
          {c.widgets.map((w, i) => (
            <div className="mock-widget" key={i}>
              <div className="label">{w.label}</div>
              {w.rows.map((r, j) => (
                <div className="mini-row" key={j}>
                  <span dangerouslySetInnerHTML={{ __html: r.left }} />
                  {r.right && <span style={{ fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: r.right }} />}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <StyledHeroFloat>
        <div className="float-icon">&#128200;</div>
        <div>
          <div className="float-label">This month</div>
          <div className="float-value">{c.floatStat}</div>
        </div>
      </StyledHeroFloat>
    </StyledBrowserMockup>
  );
}
